import React from "react";
import { Box, Grid, Card, CardContent, Typography } from "@mui/material";
import CountUp from "react-countup";

// Material UI Icons
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';

const iconMap = {
  VerifiedUser: <VerifiedUserIcon sx={{ fontSize: 50, color: "#BF0603" }} />,
  ThumbUp: <ThumbUpIcon sx={{ fontSize: 50, color: "#BF0603" }} />,
  VolunteerActivism: <VolunteerActivismIcon sx={{ fontSize: 50, color: "#BF0603" }} />,
  TravelExplore: <TravelExploreIcon sx={{ fontSize: 50, color: "#BF0603" }} />,
  Lock: <LockIcon sx={{ fontSize: 50, color: "#BF0603" }} />,
  People: <PeopleIcon sx={{ fontSize: 50, color: "#BF0603" }} />
};

export default function WhyChoose({ config }) {
  if (!config) return null;

  const { title, stats, features } = config;

  return (
    <Box 
      sx={{ 
        py: 8, 
        px: { xs: 2, md: 6 }, 
        background: "linear-gradient(180deg, #fff 0%, #fffbf8 100%)",
        position: "relative"
      }}
    >
      {/* Decorative floral watermark or shape in background */}
      <Box
        sx={{
          position: "absolute",
          top: "10%",
          right: "5%",
          width: "120px",
          height: "120px",
          opacity: 0.05,
          background: "radial-gradient(circle, #BF0603 0%, rgba(0,0,0,0) 70%)"
        }}
      />

      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          fontFamily: 'Quicksand',
          textAlign: "center",
          mb: 2,
          color: "#BF0603",
          fontSize: { xs: '26px', md: '36px' },
          letterSpacing: -0.5
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          maxWidth: "600px",
          mx: "auto",
          mb: 6,
          color: "#666",
          fontFamily: "Varela Round",
          fontSize: "1rem"
        }}
      >
        We combine modern matching algorithms with personal touch and traditional values to offer an unparalleled matrimonial service.
      </Typography>

      {/* Statistics Section */}
      {stats && (
        <Grid container spacing={4} sx={{ mb: 8, justifyContent: 'center' }}>
          {stats.map((stat, i) => (
            <Grid item xs={12} sm={4} key={i} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  textAlign: 'center',
                  px: 3,
                  py: 4,
                  width: '100%',
                  maxWidth: '280px',
                  borderRadius: '16px',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(212, 175, 55, 0.15)', // Premium light gold border
                  boxShadow: '0 8px 30px rgba(191, 6, 3, 0.04)',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '20%',
                    width: '60%',
                    height: '3px',
                    backgroundColor: '#D4AF37', // Gold line at bottom
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    color: '#BF0603',
                    fontFamily: 'Quicksand',
                    mb: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'baseline'
                  }}
                >
                  <CountUp end={stat.value} duration={2.5} enableScrollSpy scrollSpyOnce />
                  <span style={{ color: '#D4AF37' }}>{stat.suffix}</span>
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: '#444',
                    fontFamily: 'Varela Round',
                    fontSize: '0.95rem'
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Features Grid */}
      <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
        {features.map((item, i) => (
          <Grid key={i} item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card
              sx={{
                width: '100%',
                maxWidth: 360,
                borderRadius: '16px',
                padding: 3,
                backgroundColor: "#fff",
                border: "1px solid rgba(0, 0, 0, 0.04)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.03)",
                transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 12px 30px rgba(191, 6, 3, 0.08)",
                  border: "1px solid rgba(191, 6, 3, 0.15)",
                  "& .icon-box": {
                    transform: "scale(1.1) rotate(5deg)",
                    backgroundColor: "rgba(191, 6, 3, 0.07)"
                  }
                }
              }}
            >
              <Box 
                className="icon-box"
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: "50%", 
                  backgroundColor: "rgba(191, 6, 3, 0.03)",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {iconMap[item.icon] || <VerifiedUserIcon sx={{ fontSize: 50, color: "#BF0603" }} />}
              </Box>

              <CardContent sx={{ textAlign: "center", p: 0, flexGrow: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ 
                    fontWeight: 700, 
                    mb: 1.5, 
                    color: "#222", 
                    fontFamily: "Quicksand",
                    fontSize: "1.15rem"
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ 
                    color: "#555", 
                    fontFamily: "Varela Round", 
                    lineHeight: 1.6,
                    fontSize: "0.9rem" 
                  }}
                >
                  {item.desc}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
